import React, { ComponentPropsWithoutRef, HTMLAttributes } from 'react'
import cn from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

import { SButton, SArrow, SIconContainer } from './Button.styling'

type ValidTags = keyof JSX.IntrinsicElements

type ButtonProps<T extends keyof JSX.IntrinsicElements> = {
    primary?: boolean
    size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg'
    minWidth?: string
    label?: string
    iconPosition?: 'left' | 'right'
    icon?: React.ReactElement
    arrow?: boolean
    inverted?: boolean
    loading?: boolean
    as?: T | keyof JSX.IntrinsicElements
    type?: 'button' | 'submit'
} & (ComponentPropsWithoutRef<T> & HTMLAttributes<HTMLOrSVGElement>)

const DEFAULT_TAG = 'button' as const

export const Button = <T extends ValidTags = typeof DEFAULT_TAG>({
    as: tag = DEFAULT_TAG,
    primary = true,
    size = 'md',
    minWidth,
    label,
    icon,
    iconPosition,
    arrow = false,
    inverted = false,
    loading = false,
    children,
    className,
    ...props
}: ButtonProps<T>): JSX.Element => {
    const renderContent = () => {
        if (iconPosition === 'right') {
            return (
                <>
                    {label}
                    {children}
                    {icon}
                </>
            )
        } else {
            return (
                <>
                    {icon}
                    {label}
                    {children}
                </>
            )
        }
    }

    return (
        <SButton
            type="button"
            size={size}
            minWidth={minWidth}
            primary={primary}
            className={cn(className, { inverted })}
            disabled={props.disabled || loading}
            arrow={arrow}
            as={tag}
            {...props}
        >
            {renderContent()}
            {arrow && !loading && <SArrow width={19} height={16}></SArrow>}
            {loading && (
                <SIconContainer data-testid="container">
                    <FontAwesomeIcon icon={faSpinner} pulse data-testid="spinner" />
                </SIconContainer>
            )}
        </SButton>
    )
}
